/**
 * /igaming/player-segmentation — iGaming player segmentation route shell
 * (feature #158 / OPUS DASH-158).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `IgamingPlayerSegmentationPage`. Flat-file naming: dots map to path
 * separators, so `igaming.player-segmentation.tsx` mounts at
 * `/igaming/player-segmentation`.
 *
 * Search params:
 *   - `tier` — optional initial tier filter (`vip` | `casual` | `dormant`).
 *     Parsed and normalized here; the page itself owns tier state and may
 *     not yet consume the initial value, but making it a first-class search
 *     param means deep links from the ops console work today.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { IgamingPlayerSegmentationPage } from "@/features/igaming-player-segmentation/IgamingPlayerSegmentationPage";
import { PLAYER_TIERS, type PlayerTier } from "@/features/igaming-player-segmentation/api";

// ---------------------------------------------------------------------------
// Search params
// ---------------------------------------------------------------------------

interface IgamingPlayerSegmentationSearch {
	tier?: PlayerTier;
}

function isPlayerTier(v: unknown): v is PlayerTier {
	return (
		typeof v === "string" &&
		(PLAYER_TIERS as readonly string[]).includes(v.toLowerCase())
	);
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const igamingPlayerSegmentationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/igaming/player-segmentation",
	validateSearch: (
		raw: Record<string, unknown>,
	): IgamingPlayerSegmentationSearch => {
		const out: IgamingPlayerSegmentationSearch = {};
		if (isPlayerTier(raw.tier)) {
			out.tier = raw.tier.toLowerCase() as PlayerTier;
		}
		return out;
	},
	component: IgamingPlayerSegmentationPage,
});

export default igamingPlayerSegmentationRoute;

// Re-export the page so the root-owned router can `import { IgamingPlayerSegmentationPage }`
// from this module if the central wire-up prefers a single-source import.
export { IgamingPlayerSegmentationPage } from "@/features/igaming-player-segmentation/IgamingPlayerSegmentationPage";
